<script lang="ts" setup>
import { ConstantFilter, MedicalUnitFilter } from '@sancare/typescript-commons'
import { useGroupedFilterList } from '@sancare/typescript-commons/src/composables/useGroupedFilterList'
import { capitalize } from '@sancare/typescript-commons/src/misc/string-utils'
import { FilterEvent } from '@sancare/typescript-commons/src/types/filters'
import _ from 'lodash'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import FiltersData from '@/filters/FiltersData'
import { useStore } from '@/store'
import { AutomationTypeEnum } from '@/store/modules/automation/types'
import { FilterData, FilterDataTypeEnum, FilterGroupEnum, FilterValueTypeEnum } from '@/store/modules/filters/types'
import { ConditionGroup, CriterionCondition, ExistingCriterionConditionDto } from '@/store/modules/searches/types'
import { ModeEnum, PmsiTypeEnum } from '@/store/modules/settings/types'

import BiologyFilter from './BiologyFilter.vue'
import CompletionFilter from './CompletionFilter.vue'
import DateFilter from './DateFilter.vue'
import { getFilterType } from './FiltersMeta'
import RangeFilter from './RangeFilter.vue'
import SelectFilter from './SelectFilter.vue'
import TextContentCriteria from './TextContentCriteria.vue'
import TextCriteria from './TextCriteria.vue'
import { resolveCriterionToFilter } from './utils'

const store = useStore()
const { t } = useI18n()

const emit = defineEmits<{
  (e: 'add-criteria', criteria: CriterionCondition): void
  (e: 'replace-criteria', criteria: CriterionCondition): void
}>()

const props = withDefaults(defineProps<{
  automationType?: AutomationTypeEnum | null
  editingCriterion?: ExistingCriterionConditionDto | null
  isGlobal?: boolean
  isLegacy?: boolean
  mode: ModeEnum
  pmsiType: PmsiTypeEnum
}>(), {
  automationType: null,
  editingCriterion: null,
  isGlobal: false,
  isLegacy: false,
})

const filtersData = FiltersData
const filterName = ref('')

const GROUP_ORDER = [
  FilterGroupEnum.FAVORITES,
  FilterGroupEnum.DOCUMENTS,
  FilterGroupEnum.CODING,
  FilterGroupEnum.PATIENT,
  FilterGroupEnum.ACTS_AND_DIAGNOSES,
  FilterGroupEnum.STAY,
]

const FAVORITES_BY_PMSI_TYPE: Partial<Record<PmsiTypeEnum, string[]>> = {
  [PmsiTypeEnum.MCO]: ['content', 'revaloDiagnosis', 'startingGhmType', 'stayDuration', 'startingLevel'],
  [PmsiTypeEnum.SMR]: ['content', 'initialGme', 'revaloDiagnosis', 'dependency'],
}

const currentSearch = computed(() => store.state.stayList.currentSearch)
const renewalAutomationEnabled = computed(() => store.getters.flatSancareSettings?.renewal_automation)
const hasDrugs = computed(() => store.state.remoteLists.hasDrugs)
const hasHealthConstantss = computed(() => store.state.remoteLists.hasHealthConstantss)
const hasBiologyResults = computed(() => store.state.remoteLists.hasBiologyResults)
const pmsiCountry = computed(() => store.state.settings.pmsi.country)
const filterType = computed(() => filtersData[filterName.value] ? filtersData[filterName.value].type : null)
const filterLabel = computed(() => {
  if (props.isGlobal) {
    if (props.editingCriterion !== null) {
      return t('filters.editGlobalCriteria')
    }
    return t('filters.addGlobalCriteria')
  }
  if (props.editingCriterion !== null) {
    return t('filters.editCriteria')
  }
  return t('filters.addCriteria')
})

const isEditingCurrentFilter = computed(() => {
  return Boolean(
    filterValues.value
    && filterName.value === filterValues.value.filterName
  )
})

const filterValues = computed(() => {
  if (!props.editingCriterion) return null

  return resolveCriterionToFilter(props.editingCriterion.criterion, pmsiCountry.value, props.pmsiType)
})

const safeInitialValue = computed(() => {
  return isEditingCurrentFilter.value
    ? filterValues.value!.filterValue
    : null
})

const currentFilterData = computed(() => {
  if (isEditingCurrentFilter.value) {
    return filterValues.value!.filterData
  }

  return filtersData[filterName.value]
})

// The previous-stay medical unit is a "present only" global condition (no absent variant, unit only).
const isPreviousStayMedicalUnit = computed(() => currentFilterData.value?.name === 'previousStayMedicalUnit')

const filterList = computed(() => {
  const filtered = _.filter(filtersData, (filter) => {
    if (!showCriteria(filter)) {
      return false
    }

    if (!props.isLegacy) {
      const isGlobalCondition = (filter.isGlobalCondition || 'false').toString()
      if (isGlobalCondition !== 'both' && isGlobalCondition !== props.isGlobal.toString()) {
        return false
      }
    }

    if (['previousStayDiagnosis', 'previousStayMedicalUnit'].includes(filter.name) && !renewalAutomationEnabled.value) {
      return false
    }

    if (props.mode === ModeEnum.AUTOMATION) {
      return filter.mode.indexOf(props.mode) !== -1
          && filter.automationTypes.indexOf(props.automationType) !== -1
    }

    return filter.mode.indexOf(props.mode) !== -1
  })

  const filteredTranslatedList = filtered.map((filter) => ({
    ...filter,
    displayLabel: capitalize(t(filter.label, 1 + (filter.isPlurial ?? 0)))
  }))

  const sorted = filteredTranslatedList.sort((a, b) => {
    return a.displayLabel.localeCompare(b.displayLabel, 'fr', { numeric: true })
  })

  return _.keyBy(sorted, (el) => el.name)
})

const currentFavorites = computed(() => FAVORITES_BY_PMSI_TYPE[props.pmsiType] ?? [])

const groupedFilterList = useGroupedFilterList(filterList, GROUP_ORDER, currentFavorites)

function showCriteria(filterData: FilterData): boolean {
  if (!filterData.country.includes(pmsiCountry.value)) {
    return false
  }
  if (!filterData.pmsiTypes.includes(props.pmsiType)) {
    return false
  }
  switch (filterData.name) {
    case 'drug':
      return hasDrugs.value
    case 'healthConstant':
      return hasHealthConstantss.value
    case 'biology':
      return hasBiologyResults.value

    default:
      return true
  }
}

function addCriteria({ name, type, value }: FilterEvent) {
  value = value.trim()

  if ([
    'diagnosis',
    'revaloDiagnosis',
    'qaAbsentDiagnosis',
    'medicalAct',
    'qaAbsentMedicalAct',
    'exclusiveMedicalAct',
    'startingGhm',
    'currentGhm',
    'drug',
  ].includes(name)) {
    // Diagnoses are always in uppercase: K670, C059 etc...
    value = value.toUpperCase()
  }

  if (props.editingCriterion) {
    emit('replace-criteria', {
      id: props.editingCriterion.criterion.id,
      type,
      value,
    })
  } else {
    const allCriteria = currentSearch.value.criteriaGroups?.flatMap((group: ConditionGroup) => group.criteriaList ?? []) ?? []

    const alreadyExists = allCriteria.some((criterion: CriterionCondition) => criterion.type === type)

    if (props.isLegacy && getFilterType(name) === FilterValueTypeEnum.SINGLE_VALUE && alreadyExists) {
      emit('replace-criteria', { id: null, type, value })
    } else {
      emit('add-criteria', { id: null, type, value })
    }
  }

  resetForm()
}

function resetForm() {
  filterName.value = ''
}

watch(filterName, (newName, oldName) => {
  if (newName !== oldName) {
    store.commit('remoteLists/clearCompletionItems')
  }
})

watch(filterValues, (resolved) => {
  if (!resolved) return
  filterName.value = resolved.filterName
}, { immediate: true })
</script>

<template>
  <div class="row gap-2">
    <div :class="isLegacy ? ['col-md-5 col-12'] : ['col-12']">
      <div class="row align-items-center gap-2">
        <div :class="isLegacy ? ['col-md-5 col-12'] : ['col-12']">
          {{ filterLabel }} :
        </div>
        <div :class="isLegacy ? ['col'] : ['col-12']">
          <select
            v-model="filterName"
            name="filterBuilder_filterName"
            class="form-select"
            :disabled="editingCriterion"
          >
            <option
              value=""
              disabled
            >
              ---
            </option>
            <template v-if="isGlobal">
              <option
                v-for="(filter, type) in filterList"
                :key="type"
                :value="type"
              >
                {{ filter.displayLabel }}
              </option>
            </template>
            <template v-else>
              <optgroup
                v-for="(filters, groupName) in groupedFilterList"
                :key="groupName"
                :label="t(`filters.groups.${String(groupName)}`)"
              >
                <option
                  v-for="(filter, type) in filters"
                  :key="type"
                  :value="type"
                >
                  {{ filter.displayLabel }}
                </option>
              </optgroup>
            </template>
          </select>
        </div>
      </div>
    </div>
    <div :class="isLegacy ? ['col'] : ['col-12']">
      <completion-filter
        v-if="filterType === FilterDataTypeEnum.COMPLETION"
        :key="editingCriterion?.criterion?.id || filterName"
        :pmsi-type="pmsiType"
        :filter-data="currentFilterData"
        :initial-value="safeInitialValue"
        @add-filter="addCriteria"
      />
      <select-filter
        v-if="filterType === FilterDataTypeEnum.SELECT"
        :key="editingCriterion?.criterion?.id || filterName"
        :filter-data="currentFilterData"
        :initial-value="safeInitialValue"
        :is-legacy="isLegacy"
        :pmsi-country="pmsiCountry"
        :pmsi-type="pmsiType"
        @add-filter="addCriteria"
      />
      <range-filter
        v-if="filterType === FilterDataTypeEnum.RANGE"
        :key="editingCriterion?.criterion?.id || filterName"
        :filter-data="currentFilterData"
        :pmsi-type="pmsiType"
        :is-legacy="isLegacy"
        :initial-value="safeInitialValue"
        @add-filter="addCriteria"
      />
      <constant-filter
        v-if="filterType === FilterDataTypeEnum.CONSTANT"
        :key="editingCriterion?.criterion?.id || filterName"
        :model-value="safeInitialValue?.value ?? null"
        :filter-data="currentFilterData"
        :is-legacy="isLegacy"
        @add-filter="addCriteria"
      />
      <text-content-criteria
        v-if="filterType === FilterDataTypeEnum.TEXT_CONTENT"
        :key="editingCriterion?.criterion?.id || filterName"
        :filter-data="currentFilterData"
        :is-global-condition="isGlobal"
        :initial-value="safeInitialValue"
        :is-legacy="isLegacy"
        @add-filter="addCriteria"
      />
      <text-criteria
        v-if="filterType === FilterDataTypeEnum.TEXT"
        :key="editingCriterion?.criterion?.id || filterName"
        :filter-data="currentFilterData"
        :initial-value="safeInitialValue"
        :is-advanced-search="!isLegacy"
        @add-filter="addCriteria"
      />
      <medical-unit-filter
        v-if="filterType === FilterDataTypeEnum.MEDICAL_UNIT"
        :key="editingCriterion?.criterion?.id || filterName"
        :model-value="safeInitialValue?.value ?? null"
        :filter-data-name="editingCriterion?.criterion?.type ?? filtersData[filterName].name"
        :is-legacy="isLegacy"
        :has-presence-sub-filter="!isPreviousStayMedicalUnit"
        :medical-unit-only="isPreviousStayMedicalUnit || (mode === ModeEnum.AUTOMATION && automationType === AutomationTypeEnum.AI)"
        @add-filter="addCriteria"
      />
      <biology-filter
        v-if="filterType === FilterDataTypeEnum.BIOLOGY"
        :key="editingCriterion?.criterion?.id || filterName"
        :filter-data="currentFilterData"
        :is-legacy="isLegacy"
        :initial-value="safeInitialValue"
        @add-filter="addCriteria"
      />
      <date-filter
        v-if="filterType === FilterDataTypeEnum.DATE"
        :key="editingCriterion?.criterion?.id || filterName"
        :filter-data="currentFilterData"
        :is-legacy="isLegacy"
        :initial-value="safeInitialValue"
        @add-filter="addCriteria"
      />
    </div>
  </div>
</template>
